import React from "react";
import LegalLayout, { H2, H3, P, UL } from "./LegalLayout";
import SEO from "../components/SEO";
import { openCookieSettings } from "../lib/consent";

const UPDATED = "10 iunie 2026";

const TABLE_NECESSARY = [
  ["fped_cookie_consent_v1", "ai.fped.org", "12 luni", "Stochează preferințele tale privind cookie-urile."],
  ["utm_data_v1", "ai.fped.org", "12 luni", "Reține parametrii UTM pentru atribuirea sursei trafic la formularele de lead."],
];
const TABLE_ANALYTICS = [
  ["_ga", "google.com", "24 luni", "Identifică în mod anonim utilizatorii pentru Google Analytics 4."],
  ["_ga_<container-id>", "google.com", "24 luni", "Reține starea sesiunii pentru Google Analytics 4."],
  ["_clck", "clarity.ms", "12 luni", "Microsoft Clarity — păstrează un ID de utilizator anonimizat asociat sesiunii pentru heatmaps."],
  ["_clsk", "clarity.ms", "1 zi", "Microsoft Clarity — corelează evenimentele dintr-o singură sesiune (clicks, scroll)."],
  ["MUID", "clarity.ms", "13 luni", "Microsoft Clarity — identificator unic per browser folosit pentru analiza comportamentală."],
];
const TABLE_MARKETING = [
  ["_fbp", "facebook.com", "3 luni", "Folosit de Meta Pixel pentru a corela vizitele cu campaniile."],
  ["_gcl_au", "google.com", "3 luni", "Folosit de Google Ads pentru atribuirea conversiilor."],
];

export default function CookiePolicy() {
  return (
    <>
      <SEO
        title="Politica privind cookies | FPED"
        description="Politica privind cookies — ce tehnologii folosim pe ai.fped.org (GA4, Microsoft Clarity, Meta Pixel), în ce scop și cum gestionezi consimțământul."
        canonical="https://ai.fped.org/politica-cookies"
      />
      <LegalLayout
        title="Politica de cookies"
        lastUpdated={UPDATED}
        testid="cookie-policy-page"
        intro="Această pagină descrie ce cookies și tehnologii similare folosim pe ai.fped.org, în ce scop și cum îți poți gestiona consimțământul."
      >
      <H2>1. Ce sunt cookies-urile?</H2>
      <P>
        Cookies-urile sunt fișiere mici de text plasate pe dispozitivul tău atunci când vizitezi
        un site web. Ele permit site-ului să rețină informații despre vizita ta (preferințe,
        sesiune, date analitice), pentru o experiență mai relevantă și mai sigură.
      </P>

      <H2>2. Categorii de cookies pe care le folosim</H2>

      <H3>2.1. Cookies necesare (întotdeauna active)</H3>
      <P>Sunt esențiale pentru funcționarea site-ului și pentru reținerea opțiunilor tale de consimțământ. Nu pot fi dezactivate.</P>
      <CookieTable rows={TABLE_NECESSARY} />

      <H3>2.2. Cookies de analiză (pe bază de consimțământ)</H3>
      <P>
        Folosim <strong>Google Analytics 4</strong> pentru a înțelege, în mod agregat și anonim,
        cum este folosit site-ul. IP-ul este anonimizat. Datele nu sunt folosite pentru a te
        identifica personal.
      </P>
      <P>
        Folosim, de asemenea, <strong>Microsoft Clarity</strong> pentru analiză comportamentală
        și optimizarea experienței utilizatorilor. Clarity colectează informații privind
        click-uri, scroll, navigare și interacțiuni cu paginile, sub formă de heatmaps și
        înregistrări de sesiune. <strong>Datele introduse în formulare sunt mascate automat</strong>,
        iar zonele sensibile (checkout, paginile de plată, formularele de înscriere webinar,
        pagina de confirmare comandă și dashboard-ul administrativ) sunt marcate explicit cu
        clasa <code className="px-1 bg-[#F5F6F6] rounded text-xs">clarity-mask</code> astfel
        încât conținutul lor să apară obfuscat în înregistrări. Clarity este activat exclusiv
        după acceptarea cookie-urilor de analiză. Furnizor: <strong>Microsoft Ireland Operations Ltd.</strong>.
        Detalii: <a className="text-[#965EA7] underline" href="https://privacy.microsoft.com/privacystatement" target="_blank" rel="noopener noreferrer">privacy.microsoft.com</a>.
      </P>
      <CookieTable rows={TABLE_ANALYTICS} />

      <H3>2.3. Cookies de marketing (pe bază de consimțământ)</H3>
      <P>
        Folosim <strong>Meta Pixel</strong> și <strong>Google Ads</strong> pentru a măsura
        eficiența campaniilor publicitare și pentru a-ți afișa anunțuri relevante pe alte
        platforme. Aceste cookies sunt activate doar dacă acorzi consimțământul.
      </P>
      <CookieTable rows={TABLE_MARKETING} />

      <H2>3. Google Consent Mode v2</H2>
      <P>
        Implementăm <strong>Google Consent Mode v2</strong> cu setări implicite „denied” pentru
        toate categoriile non-esențiale. Asta înseamnă că, până la consimțământul tău explicit,
        nu sunt setate cookies de analiză sau marketing. La acordarea consimțământului, scripturile
        corespunzătoare sunt încărcate și activate.
      </P>

      <H2>4. Cum îți gestionezi consimțământul?</H2>
      <P>
        La prima vizită îți afișăm un banner cu trei opțiuni egal vizibile:{" "}
        <strong>Accept toate</strong>, <strong>Refuz toate</strong> și <strong>Personalizează</strong>.
        Poți modifica oricând aceste preferințe folosind butonul{" "}
        <button
          type="button"
          onClick={openCookieSettings}
          className="underline text-[#965EA7] hover:text-[#7A4986]"
          data-testid="open-cookie-settings"
        >
          Setări cookies
        </button>
        {" "}din această pagină sau din footer-ul site-ului.
      </P>
      <P>
        Poți, de asemenea, șterge cookies din setările browser-ului tău. Reține că dezactivarea
        cookies-urilor necesare poate afecta funcționarea site-ului.
      </P>

      <H2>5. Servicii terțe</H2>
      <P>
        Cookies-urile de analiză și marketing sunt setate de terți (Google, Meta) pe propriile
        domenii. Pentru detalii suplimentare consultă politicile lor:
      </P>
      <UL>
        <li><a className="text-[#965EA7] underline" href="https://policies.google.com/technologies/cookies" target="_blank" rel="noopener noreferrer">Google – Cookies and similar technologies</a></li>
        <li><a className="text-[#965EA7] underline" href="https://www.facebook.com/privacy/policies/cookies" target="_blank" rel="noopener noreferrer">Meta – Cookies Policy</a></li>
        <li><a className="text-[#965EA7] underline" href="https://privacy.microsoft.com/privacystatement" target="_blank" rel="noopener noreferrer">Microsoft – Privacy Statement (Clarity)</a></li>
        <li><a className="text-[#965EA7] underline" href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer">Cloudflare – Privacy Policy</a></li>
      </UL>

      <H2>6. Cloudflare Turnstile (anti-spam)</H2>
      <P>
        Pentru a proteja formularele site-ului împotriva trimiterilor automate (boți, spam),
        folosim <strong>Cloudflare Turnstile</strong> – un serviciu CAPTCHA invizibil care
        analizează semnale tehnice ale browserului tău (fără urmărire publicitară).
        Acest serviciu este considerat <em>strict necesar</em> pentru securitatea site-ului
        și se încarcă atunci când completezi un formular, indiferent de preferințele tale
        privind cookie-urile de analiză sau marketing.
      </P>
      <P>
        Cloudflare poate prelucra adresa ta IP și un token criptografic temporar pentru
        validarea anti-abuz. Datele nu sunt folosite în scopuri publicitare. Detalii:{" "}
        <a className="text-[#965EA7] underline" href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer">cloudflare.com/privacypolicy</a>.
      </P>

      <H2>7. Procesatori de plăți</H2>
      <P>
        Plățile cu cardul sunt procesate exclusiv prin furnizori externi autorizați. În funcție
        de configurația activă la momentul tranzacției, plata se realizează prin{" "}
        <strong>NETOPIA Payments</strong> (NETOPIA Financial Services S.A., România) sau{" "}
        <strong>EuPlătesc</strong> (EuroPayment Services S.R.L., România). Aplicația noastră{" "}
        <strong>nu stochează datele cardului bancar</strong>; acestea sunt introduse și procesate
        direct în mediul securizat al furnizorului de plată ales, conform standardelor PCI-DSS.
      </P>
      <P>
        Furnizorii de plată pot seta cookies tehnice strict necesare în propriile lor pagini,
        pentru securitatea tranzacției și prevenția fraudelor. Aceste cookies sunt guvernate de
        politicile lor:{" "}
        <a className="text-[#965EA7] underline" href="https://netopia-payments.com/politica-de-confidentialitate/" target="_blank" rel="noopener noreferrer">NETOPIA – Politica de confidențialitate</a>,{" "}
        <a className="text-[#965EA7] underline" href="https://www.euplatesc.ro/politica-de-confidentialitate/" target="_blank" rel="noopener noreferrer">EuPlătesc – Politica de confidențialitate</a>.
      </P>

      <H2>8. Emailuri tranzacționale (Resend)</H2>
      <P>
        Emailurile tranzacționale și de informare (confirmare înregistrare, confirmare plată,
        confirmări înscriere la webinar, notificări de cont) sunt trimise prin serviciul{" "}
        <strong>Resend</strong> (Resend Inc., SUA). Adresa ta de email este procesată exclusiv
        în acest scop, pe baza executării contractului sau a consimțământului acordat la
        completarea formularelor. Resend nu folosește datele tale pentru publicitate. Detalii:{" "}
        <a className="text-[#965EA7] underline" href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">resend.com/legal/privacy-policy</a>.
      </P>

      <H2>9. Modificări</H2>
      <P>
        Putem actualiza această politică pentru a reflecta schimbări tehnice sau legale.
        Versiunea curentă este afișată mereu pe această pagină, împreună cu data ultimei actualizări.
      </P>
    </LegalLayout>
    </>
  );
}

function CookieTable({ rows }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-[#E5E7EB] mt-2">
      <table className="w-full text-sm">
        <thead className="bg-[#F5F6F6] text-[#373A45]">
          <tr>
            <th className="text-left px-4 py-2 font-semibold">Nume</th>
            <th className="text-left px-4 py-2 font-semibold">Domeniu</th>
            <th className="text-left px-4 py-2 font-semibold">Durată</th>
            <th className="text-left px-4 py-2 font-semibold">Scop</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-[#E5E7EB]">
              <td className="px-4 py-2 font-mono text-xs">{r[0]}</td>
              <td className="px-4 py-2">{r[1]}</td>
              <td className="px-4 py-2">{r[2]}</td>
              <td className="px-4 py-2 text-[#6B7280]">{r[3]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
